import { GoogleGenAI, LiveServerMessage, Modality, Type, FunctionDeclaration } from "@google/genai";
import { IkigaiBuckets } from "../types";
import { arrayBufferToBase64, decodeAudioData, float32ToInt16, base64ToUint8Array } from "../utils/audioUtils";

const MODEL_NAME = 'gemini-2.5-flash-native-audio-preview-09-2025';

const updateDashboardTool: FunctionDeclaration = {
    name: 'update_dashboard',
    description: 'Update the Ikigai dashboard buckets with new findings about passion, mission, profession, or vocation.',
    parameters: {
        type: Type.OBJECT,
        properties: {
            passion: { type: Type.ARRAY, items: { type: Type.STRING } },
            mission: { type: Type.ARRAY, items: { type: Type.STRING } },
            profession: { type: Type.ARRAY, items: { type: Type.STRING } },
            vocation: { type: Type.ARRAY, items: { type: Type.STRING } }
        }
    }
};

interface LiveSessionConfig {
    onDashboardUpdate: (updates: Partial<IkigaiBuckets>) => void;
    onClose: () => void;
    onError: (error: Error) => void;
}

export class KaiLiveSession {
    private ai: GoogleGenAI;
    private config: LiveSessionConfig;
    private inputContext: AudioContext | null = null;
    private outputContext: AudioContext | null = null;
    private outputAnalyser: AnalyserNode | null = null;
    private stream: MediaStream | null = null;
    private processor: ScriptProcessorNode | null = null;
    private source: MediaStreamAudioSourceNode | null = null;
    private nextStartTime = 0;
    private sources = new Set<AudioBufferSourceNode>();
    private isConnected = false;

    constructor(apiKey: string, config: LiveSessionConfig) {
        this.ai = new GoogleGenAI({ apiKey });
        this.config = config;
    }

    async connect() {
        if (this.isConnected) return;

        try {
            // Check if media devices are supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("Microphone access is not supported in this browser.");
            }

            // 1. Get Microphone FIRST
            try {
                this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (err) {
                console.error("Error accessing microphone:", err);
                throw new Error("Microphone not found or access denied. Please check your system settings.");
            }

            // 2. Setup Audio Contexts
            this.inputContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
            this.outputContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

            // Setup Analyser for visualization
            this.outputAnalyser = this.outputContext.createAnalyser();
            this.outputAnalyser.fftSize = 512;
            this.outputAnalyser.smoothingTimeConstant = 0.5;
            this.outputAnalyser.connect(this.outputContext.destination);

            // 3. Connect to Gemini Live
            const sessionPromise = this.ai.live.connect({
                model: MODEL_NAME,
                callbacks: {
                    onopen: () => {
                        console.log("Live Session Opened");
                        this.isConnected = true;
                        this.startAudioInput(sessionPromise);
                    },
                    onmessage: (msg: LiveServerMessage) => this.handleMessage(msg, sessionPromise),
                    onclose: () => {
                        console.log("Live Session Closed");
                        this.cleanup();
                        this.config.onClose();
                    },
                    onerror: (e) => {
                        console.error("Live Session Error", e);
                        this.config.onError(new Error("Connection error"));
                    }
                },
                config: {
                    responseModalities: [Modality.AUDIO],
                    systemInstruction: {
                        parts: [{
                            text: `You are Kai, an expert Ikigai Life Coach. We are in a "Deep Dive" voice session.
                            - Speak with a warm, calm, and soothing tone.
                            - Keep responses concise (1-3 sentences) to allow for a fluid conversation.
                            - Do NOT list the buckets explicitly in speech. Focus on the feelings and introspective questions.
                            - If the user mentions something that belongs in Passion, Mission, Profession, or Vocation, call the "update_dashboard" tool silently.
                            - Be empathetic and curious.`
                        }]
                    },
                    tools: [{ functionDeclarations: [updateDashboardTool] }]
                }
            });

        } catch (error) {
            console.error("Failed to start live session:", error);
            this.cleanup();
            this.config.onError(error as Error);
        }
    }

    getOutputAnalyser(): AnalyserNode | null {
        return this.outputAnalyser;
    }

    private startAudioInput(sessionPromise: Promise<any>) {
        if (!this.inputContext || !this.stream) return;

        this.source = this.inputContext.createMediaStreamSource(this.stream);
        this.processor = this.inputContext.createScriptProcessor(4096, 1, 1);

        this.processor.onaudioprocess = (e) => {
            const inputData = e.inputBuffer.getChannelData(0);
            const pcmInt16 = float32ToInt16(inputData);
            // Force cast SharedArrayBuffer to ArrayBuffer to satisfy strict TS check
            const base64Data = arrayBufferToBase64(pcmInt16.buffer as unknown as ArrayBuffer);

            sessionPromise.then(session => {
                session.sendRealtimeInput({
                    media: {
                        mimeType: 'audio/pcm;rate=16000',
                        data: base64Data
                    }
                });
            });
        };

        this.source.connect(this.processor);
        this.processor.connect(this.inputContext.destination);
    }

    private async handleMessage(message: LiveServerMessage, sessionPromise: Promise<any>) {
        // Handle Audio
        const audioData = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
        if (audioData && this.outputContext) {
            this.playAudioChunk(audioData);
        }

        // Handle Tool Calls (Dashboard Updates)
        if (message.toolCall?.functionCalls) {
            for (const call of message.toolCall.functionCalls) {
                if (call.name === 'update_dashboard') {
                    console.log("Updating dashboard from Voice:", call.args);
                    this.config.onDashboardUpdate(call.args as unknown as Partial<IkigaiBuckets>);

                    // Send tool response
                    sessionPromise.then(session => {
                        session.sendToolResponse({
                            functionResponses: [{
                                id: call.id,
                                name: call.name,
                                response: { result: 'Dashboard updated' }
                            }]
                        });
                    });
                }
            }
        }

        // Handle Interruption
        if (message.serverContent?.interrupted) {
            this.stopAllAudio();
        }
    }

    private async playAudioChunk(base64Data: string) {
        if (!this.outputContext) return;

        try {
            const uint8 = base64ToUint8Array(base64Data);
            const audioBuffer = await decodeAudioData(uint8, this.outputContext, 24000, 1);

            this.nextStartTime = Math.max(this.outputContext.currentTime, this.nextStartTime);

            const source = this.outputContext.createBufferSource();
            source.buffer = audioBuffer;

            // Connect to Analyser (which is already connected to Destination)
            if (this.outputAnalyser) {
                source.connect(this.outputAnalyser);
            } else {
                source.connect(this.outputContext.destination);
            }

            source.onended = () => this.sources.delete(source);
            source.start(this.nextStartTime);

            this.sources.add(source);
            this.nextStartTime += audioBuffer.duration;
        } catch (e) {
            console.error("Error decoding/playing audio", e);
        }
    }

    private stopAllAudio() {
        this.sources.forEach(s => {
            try { s.stop(); } catch (e) { }
        });
        this.sources.clear();
        if (this.outputContext) {
            this.nextStartTime = this.outputContext.currentTime;
        }
    }

    disconnect() {
        this.isConnected = false;
        this.cleanup();
    }

    private cleanup() {
        if (this.source) {
            try { this.source.disconnect(); } catch (e) { }
            this.source = null;
        }
        if (this.processor) {
            try { this.processor.disconnect(); } catch (e) { }
            this.processor = null;
        }
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
        if (this.outputAnalyser) {
            try { this.outputAnalyser.disconnect(); } catch (e) { }
            this.outputAnalyser = null;
        }
        if (this.inputContext) {
            try { this.inputContext.close(); } catch (e) { }
            this.inputContext = null;
        }
        if (this.outputContext) {
            try { this.outputContext.close(); } catch (e) { }
            this.outputContext = null;
        }
        this.stopAllAudio();
    }
}